/**
 * Narrowing a platform locale to an interface locale we actually ship.
 *
 * Every client has to do this and each one got it differently wrong. A platform hands
 * back a full BCP-47 tag — `en-US`, `pt_BR`, `zh-Hant-TW` — and the contract accepts a
 * short list of bare language codes. Send the tag unchanged and sign-in is a 400, which
 * is not a degraded experience but a dead app: no token, no course, no screen.
 *
 * iOS shipped that bug and fixed it in its own networking layer. Android shipped the
 * same bug and still had it: `Locale.getDefault().toLanguageTag()` is `en-US` on a US
 * device, so the Android app could not sign in on essentially any real device. No test
 * caught it on either platform, because the fixtures accept whatever the client sends —
 * only pointing a built app at a real server shows it.
 *
 * So the rule lives here once, and is ported to Swift and Kotlin against the shared
 * case table generated from `CASES` below.
 */

/**
 * The interface languages we ship. `zh` keeps its script subtag because simplified and
 * traditional are genuinely different interfaces; no other language needs one.
 */
export const INTERFACE_LOCALES = [
  'en',
  'es',
  'pt',
  'fr',
  'de',
  'it',
  'nl',
  'pl',
  'tr',
  'ru',
  'uk',
  'ar',
  'he',
  'hi',
  'id',
  'vi',
  'th',
  'ja',
  'ko',
  'zh-Hans',
  'zh-Hant',
] as const;

export type InterfaceLocale = (typeof INTERFACE_LOCALES)[number];

export const DEFAULT_INTERFACE_LOCALE: InterfaceLocale = 'en';

const SUPPORTED = new Set<string>(INTERFACE_LOCALES);

/**
 * Regions that use traditional Chinese. A `zh` tag with no script subtag is ambiguous,
 * and the region is the only other evidence available.
 */
const TRADITIONAL_REGIONS = new Set(['TW', 'HK', 'MO']);

/** Java's Locale still emits the pre-1989 codes for these. */
const LEGACY_CODES: Readonly<Record<string, string>> = { iw: 'he', in: 'id', ji: 'yi' };

/**
 * Narrows any platform locale tag to one we ship, falling back to English.
 *
 * Accepts the separators platforms actually use (`en-US`, `en_US`) and the legacy
 * Android codes, which `Locale` still returns on some devices.
 */
export function toInterfaceLocale(tag: string | null | undefined): InterfaceLocale {
  if (tag === null || tag === undefined) return DEFAULT_INTERFACE_LOCALE;

  const parts = tag
    .replace(/_/g, '-')
    .split('-')
    .filter((part) => part.length > 0);
  const rawLanguage = (parts[0] ?? '').toLowerCase();
  if (rawLanguage.length === 0) return DEFAULT_INTERFACE_LOCALE;

  const language = LEGACY_CODES[rawLanguage] ?? rawLanguage;

  if (language === 'zh') {
    const script = parts.find((part) => part.length === 4);
    if (script !== undefined) {
      return script.toLowerCase() === 'hant' ? 'zh-Hant' : 'zh-Hans';
    }
    const region = parts
      .slice(1)
      .find((part) => part.length === 2)
      ?.toUpperCase();
    return region !== undefined && TRADITIONAL_REGIONS.has(region) ? 'zh-Hant' : 'zh-Hans';
  }

  return SUPPORTED.has(language) ? (language as InterfaceLocale) : DEFAULT_INTERFACE_LOCALE;
}

/**
 * The cases all three platforms are held to. Generated into the mobile test resources,
 * so a port that disagrees fails its own build rather than failing a learner's sign-in.
 */
export const CASES: readonly { readonly input: string; readonly expected: InterfaceLocale }[] = [
  // The bug itself: a region subtag must not survive.
  { input: 'en-US', expected: 'en' },
  { input: 'en_US', expected: 'en' },
  { input: 'en-GB', expected: 'en' },
  { input: 'es-MX', expected: 'es' },
  { input: 'es_419', expected: 'es' },
  { input: 'pt-BR', expected: 'pt' },
  { input: 'pt_PT', expected: 'pt' },
  { input: 'fr-CA', expected: 'fr' },
  { input: 'de-AT', expected: 'de' },
  // Already narrow.
  { input: 'en', expected: 'en' },
  { input: 'ja', expected: 'ja' },
  { input: 'ko', expected: 'ko' },
  // Chinese: script wins over region; region decides when there is no script.
  { input: 'zh-Hans', expected: 'zh-Hans' },
  { input: 'zh-Hant', expected: 'zh-Hant' },
  { input: 'zh-Hant-TW', expected: 'zh-Hant' },
  { input: 'zh-Hans-CN', expected: 'zh-Hans' },
  { input: 'zh-CN', expected: 'zh-Hans' },
  { input: 'zh-TW', expected: 'zh-Hant' },
  { input: 'zh-HK', expected: 'zh-Hant' },
  { input: 'zh', expected: 'zh-Hans' },
  // Legacy Java codes.
  { input: 'iw-IL', expected: 'he' },
  { input: 'in-ID', expected: 'id' },
  // Platforms are inconsistent about case.
  { input: 'EN-us', expected: 'en' },
  { input: 'PT-br', expected: 'pt' },
  // Languages we do not ship fall back rather than failing the request.
  { input: 'sv-SE', expected: 'en' },
  { input: 'cy', expected: 'en' },
  { input: 'yi', expected: 'en' },
  // Degenerate input must still produce a valid locale.
  { input: '', expected: 'en' },
  { input: '-', expected: 'en' },
  { input: 'und', expected: 'en' },
];
